// pages/admin/CompanySettings.js
// הגדרות חברה — 4 tabs
//
// Tab 0 (כללי): שם חברה + לוגו (drag & drop + preview + delete)
// Tab 1 (גיבויים): create, list, restore, upload, download, delete — response 7
// Tab 2 (נתוני דמה + איפוס): demo-data + system reset — response 8
// Tab 3 (לוגי מערכת): debug logs viewer — response 8
//
// TODO v2.0: Company-wide default theme tab (requires Backend field in company_settings)
//
// API (Tab 0):
//   GET /company → { company_name, logo_path, logo_url }
//   PUT /company { company_name } → update name
//   POST /company/logo (multipart, field: logo) → upload logo
//   DELETE /company/logo → remove logo

import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Paper,
  Alert,
  Snackbar,
  IconButton,
  LinearProgress,
  Divider,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '../../components/shared';
import { useApi, useApiMutation } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFile } from '../../services/api';
import BackupTab from '../../components/admin/BackupTab';
import DemoResetTab from '../../components/admin/DemoResetTab';
import LogsTab from '../../components/admin/LogsTab';

// =============================================================================
// Tab panel helper
// =============================================================================
function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

// =============================================================================
// CompanySettings Page
// =============================================================================
export default function CompanySettings() {
  const { t } = useTranslation();
  const { isSuperAdmin } = useAuth();

  // Active tab
  const [activeTab, setActiveTab] = useState(0);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ===================================================================
  // Tab 0: General — Company Name + Logo
  // ===================================================================
  const { data: company, loading: companyLoading, error: companyError, refetch: refetchCompany } = useApi('/company', {
    defaultData: null,
  });

  const { mutate, loading: mutating } = useApiMutation();

  // Company name form
  const [companyName, setCompanyName] = useState('');
  const [nameInitialized, setNameInitialized] = useState(false);
  const [nameError, setNameError] = useState('');

  // Initialize name from API data
  if (company?.company_name && !nameInitialized) {
    setCompanyName(company.company_name);
    setNameInitialized(true);
  }

  // Logo upload state
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [deleteLogoOpen, setDeleteLogoOpen] = useState(false);
  const fileInputRef = useRef(null);

  // ===================================================================
  // Save company name
  // ===================================================================
  const handleSaveName = useCallback(async () => {
    if (mutating) return;
    const trimmed = companyName.trim();
    if (!trimmed) {
      setNameError(t('common.required'));
      return;
    }
    setNameError('');
    const result = await mutate('put', '/company', { company_name: trimmed });
    if (result.success) {
      showSnackbar(t('companySettings.settingsSaved'));
      refetchCompany();
    } else {
      setNameError(result.error || t('common.error'));
    }
  }, [companyName, mutating, mutate, refetchCompany, showSnackbar, t]);

  // ===================================================================
  // Logo upload
  // ===================================================================
  const handleLogoUpload = useCallback(async (file) => {
    if (!file || uploading) return;

    // Validate type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      showSnackbar(t('companySettings.logoHelp'), 'error');
      return;
    }

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar(t('companySettings.logoHelp'), 'error');
      return;
    }

    setUploading(true);
    try {
      await uploadFile('/company/logo', file, 'logo');
      showSnackbar(t('companySettings.settingsSaved'));
      refetchCompany();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || t('common.error');
      showSnackbar(msg, 'error');
    } finally {
      setUploading(false);
    }
  }, [uploading, refetchCompany, showSnackbar, t]);

  // File input change
  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleLogoUpload(file);
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleLogoUpload]);

  // Drag & drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleLogoUpload(file);
  }, [handleLogoUpload]);

  // ===================================================================
  // Delete logo
  // ===================================================================
  const handleDeleteLogo = useCallback(async () => {
    if (mutating) return;
    const result = await mutate('delete', '/company/logo');
    setDeleteLogoOpen(false);
    if (result.success) {
      showSnackbar(t('companySettings.settingsSaved'));
      refetchCompany();
    } else {
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [mutating, mutate, refetchCompany, showSnackbar, t]);

  // ===================================================================
  // Tab definitions
  // ===================================================================
  const tabs = useMemo(() => {
    const list = [
      { label: t('companySettings.tabs.general'), testId: 'settings__tab--general' },
    ];
    // Backup, demo, logs — super admin only
    if (isSuperAdmin) {
      list.push(
        { label: t('companySettings.tabs.backup'), testId: 'settings__tab--backup' },
        { label: t('companySettings.tabs.demoData'), testId: 'settings__tab--demo-reset' },
        { label: t('companySettings.tabs.logs'), testId: 'settings__tab--logs' },
      );
    }
    return list;
  }, [isSuperAdmin, t]);

  // Has logo?
  const hasLogo = !!(company?.logo_url || company?.logo_path);

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="settings__page" sx={{ pb: 4 }}>
      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SettingsIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          {t('companySettings.title')}
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        data-testid="settings__tabs"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {tabs.map((tab, i) => (
          <Tab key={i} label={tab.label} data-testid={tab.testId} />
        ))}
      </Tabs>

      {/* ============================================================= */}
      {/* Tab 0: General — Company Name + Logo                          */}
      {/* ============================================================= */}
      <TabPanel value={activeTab} index={0}>
        {(companyLoading || uploading) && <LinearProgress sx={{ mb: 2 }} />}

        {companyError && (
          <Alert severity="error" sx={{ mb: 2 }}>{companyError}</Alert>
        )}

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          {/* Company name */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('companySettings.companyName')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
            <TextField
              data-testid="settings__input--company-name"
              value={companyName}
              onChange={(e) => { setCompanyName(e.target.value); setNameError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
              fullWidth
              size="small"
              disabled={mutating || companyLoading}
              error={!!nameError}
              helperText={nameError}
              placeholder={t('companySettings.companyName')}
            />
            <Button
              data-testid="settings__button--save-name"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveName}
              disabled={mutating || !companyName.trim() || companyName.trim() === company?.company_name}
              sx={{ minWidth: 100, whiteSpace: 'nowrap' }}
            >
              {mutating ? t('common.saving') : t('common.save')}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Logo */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('companySettings.logo')}
          </Typography>

          {/* Current logo preview */}
          {hasLogo && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={company.logo_url || company.logo_path}
                alt={t('companySettings.logo')}
                data-testid="settings__img--logo-preview"
                sx={{
                  maxWidth: 200,
                  maxHeight: 100,
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                }}
              />
              <Button
                data-testid="settings__button--remove-logo"
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteLogoOpen(true)}
                disabled={mutating || uploading}
              >
                {t('companySettings.removeLogo')}
              </Button>
            </Box>
          )}

          {/* Drag & drop zone */}
          <Box
            data-testid="settings__drop-zone--logo"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: uploading ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              bgcolor: dragActive ? 'action.hover' : 'transparent',
              '&:hover': {
                borderColor: 'primary.light',
                bgcolor: 'action.hover',
              },
            }}
          >
            {uploading ? (
              <LinearProgress sx={{ width: '60%', mx: 'auto' }} />
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('companySettings.dragDropLogo')}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                  {t('companySettings.logoHelp')}
                </Typography>
              </>
            )}
          </Box>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Paper>
      </TabPanel>

      {/* ============================================================= */}
      {/* Tab 1: Backup                                                  */}
      {/* ============================================================= */}
      <TabPanel value={activeTab} index={1}>
        <BackupTab showSnackbar={showSnackbar} />
      </TabPanel>

      {/* ============================================================= */}
      {/* Tab 2: Demo Data + System Reset                                */}
      {/* ============================================================= */}
      <TabPanel value={activeTab} index={2}>
        <DemoResetTab showSnackbar={showSnackbar} />
      </TabPanel>

      {/* ============================================================= */}
      {/* Tab 3: System Logs                                             */}
      {/* ============================================================= */}
      <TabPanel value={activeTab} index={3}>
        <LogsTab showSnackbar={showSnackbar} />
      </TabPanel>

      {/* ============================================================= */}
      {/* Delete Logo Confirm                                            */}
      {/* ============================================================= */}
      <ConfirmDialog
        open={deleteLogoOpen}
        title={t('companySettings.removeLogo')}
        message={t('companySettings.removeLogoConfirm')}
        onConfirm={handleDeleteLogo}
        onCancel={() => setDeleteLogoOpen(false)}
        confirmLabel={t('common.delete')}
        destructive
        loading={mutating}
      />

      {/* ============================================================= */}
      {/* Snackbar                                                       */}
      {/* ============================================================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
